import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, ExternalLink, FileDown, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PLANS } from "@/lib/plans";
import { useAuth } from "@/context/AuthContext";
import { CS } from "@/constants/testIds/clickshield";

const Card = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="rounded-xl bg-[#0B101A] border border-white/8 p-6"
  >
    {children}
  </motion.div>
);

export const Billing = () => {
  const { user } = useAuth();
  const [portalBusy, setPortalBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/reports/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clickshield-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not generate the PDF report");
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    api.get("/reports/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ weekly_enabled: false, alerts_enabled: true, last_sent: null }));
  }, []);

  const plan = PLANS.find((p) => p.id === user?.plan);
  const planLabel = plan ? plan.name : "Trial";

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPortalBusy(false);
    }
  };

  const toggleWeekly = async (checked) => {
    setSettings((s) => ({ ...s, weekly_enabled: checked }));
    try {
      await api.patch("/reports/settings", { weekly_enabled: checked });
      toast.success(checked ? "Weekly reports enabled" : "Weekly reports paused");
    } catch {
      setSettings((s) => ({ ...s, weekly_enabled: !checked }));
      toast.error("Could not update report settings");
    }
  };

  const toggleAlerts = async (checked) => {
    setSettings((s) => ({ ...s, alerts_enabled: checked }));
    try {
      await api.patch("/reports/settings", { alerts_enabled: checked });
      toast.success(checked ? "Threat alerts enabled" : "Threat alerts paused");
    } catch {
      setSettings((s) => ({ ...s, alerts_enabled: !checked }));
      toast.error("Could not update alert settings");
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/send-now");
      setSettings((s) => ({ ...s, last_sent: data.sent_at }));
      toast.success(`Report sent to ${data.recipient}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start">
      <Card>
        <div data-testid={CS.billingPlanCard}>
          <div className="flex items-center gap-2 mb-5">
            <CreditCard className="w-4 h-4 text-cyan-neon" />
            <h3 className="font-display text-lg text-white">Subscription</h3>
          </div>
          <div className="rounded-lg bg-[#040914] border border-white/8 p-5 mb-5">
            <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Current plan</div>
            <div className="flex items-end justify-between mt-1">
              <div className="font-display text-2xl text-cyan-neon">{planLabel}</div>
              {plan && <div className="font-mono text-sm text-slate-400">from ${plan.yearly}/mo</div>}
            </div>
            <div className="font-mono text-[10px] text-[#39FF14] mt-1 uppercase">{user?.plan_status}</div>
          </div>
          <button
            data-testid={CS.billingManageBtn}
            onClick={openPortal}
            disabled={portalBusy}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-cyan-neon text-[#040914] font-semibold text-sm py-3 hover:brightness-110 transition-[filter] disabled:opacity-60"
          >
            {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Manage subscription
          </button>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            Upgrade, downgrade or cancel your plan, update payment details and download invoices — all in the secure Stripe billing portal.
          </p>
        </div>
      </Card>

      <Card delay={0.06}>
        <div className="flex items-center gap-2 mb-5">
          <Mail className="w-4 h-4 text-[#39FF14]" />
          <h3 className="font-display text-lg text-white">Email reports</h3>
        </div>
        {!settings ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 text-cyan-neon animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg bg-[#040914] border border-white/8 p-5 mb-4">
              <div>
                <div className="text-sm text-white">Weekly fraud-savings summary</div>
                <div className="text-xs text-slate-500 mt-0.5">Delivered every week to {user?.email}</div>
              </div>
              <Switch
                data-testid={CS.reportWeeklyToggle}
                checked={settings.weekly_enabled}
                onCheckedChange={toggleWeekly}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[#040914] border border-white/8 p-5 mb-4">
              <div>
                <div className="text-sm text-white">Instant threat alerts</div>
                <div className="text-xs text-slate-500 mt-0.5">Email me when an attack spike hits (max 1/hour)</div>
              </div>
              <Switch
                data-testid="report-alerts-toggle"
                checked={settings.alerts_enabled}
                onCheckedChange={toggleAlerts}
              />
            </div>
            <button
              data-testid={CS.reportSendNowBtn}
              onClick={sendNow}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#39FF14]/30 text-[#39FF14] font-semibold text-sm py-3 hover:bg-[#39FF14]/10 transition-colors disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Send report now"}
            </button>
            <button
              data-testid={CS.billingPdfBtn}
              onClick={downloadPdf}
              disabled={pdfBusy}
              className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-cyan-neon/30 text-cyan-neon font-semibold text-sm py-3 hover:bg-cyan-neon/10 transition-colors disabled:opacity-60"
            >
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {pdfBusy ? "Generating…" : "Download client-ready PDF"}
            </button>
            <p data-testid={CS.reportLastSent} className="font-mono text-[11px] text-slate-500 mt-3">
              {settings.last_sent
                ? `Last sent ${new Date(settings.last_sent).toLocaleString()}`
                : "No report sent yet"}
            </p>
          </>
        )}
      </Card>
    </div>
  );
};
